import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  CreateFeedbackRequestRequest,
  CompleteFeedbackRequestRequest,
  FeedbackRequest,
} from './types';

// ============================================================================
// Feedback API
// ============================================================================

export const feedbackApi = createApi({
  reducerPath: 'marketplaceFeedbackApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    CACHE_TAGS.FeedbackRequests,
    CACHE_TAGS.Reviews,
    CACHE_TAGS.Profile,
    CACHE_TAGS.MarketplaceNotifications,
  ],
  endpoints: (builder) => ({
    /**
     * Create a feedback request (seller initiates after marking item as sold)
     */
    createFeedbackRequest: builder.mutation<{ id: string }, CreateFeedbackRequestRequest>({
      query: (data) => ({
        url: MARKETPLACE_API.feedbackRequests.base,
        method: 'POST',
        body: {
          listingId: Number(data.listingId),
          buyerId: Number(data.buyerId),
        },
      }),
      transformResponse: (response: ApiResponse<{ id: number }> | null) => {
        if (!response || !response.data) {
          console.warn('createFeedbackRequest: Empty or null response from API');
          return { id: '' };
        }
        return { id: String(response.data.id) };
      },
      invalidatesTags: [{ type: CACHE_TAGS.FeedbackRequests, id: 'LIST' }],
    }),

    /**
     * Get pending feedback requests for current user (as buyer)
     */
    getPendingFeedbackRequests: builder.query<FeedbackRequest[], void>({
      query: () => ({
        url: MARKETPLACE_API.feedbackRequests.pending,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<FeedbackRequest[]> | null): FeedbackRequest[] => {
        if (!response || !response.data) {
          console.warn('getPendingFeedbackRequests: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'FeedbackRequests' as const, id })),
              { type: CACHE_TAGS.FeedbackRequests, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.FeedbackRequests, id: 'LIST' }],
    }),

    /**
     * Complete a feedback request (buyer submits rating/review)
     */
    completeFeedbackRequest: builder.mutation<{ reviewId: string }, CompleteFeedbackRequestRequest>({
      query: ({ requestId, rating, text }) => ({
        url: MARKETPLACE_API.feedbackRequests.complete(requestId),
        method: 'POST',
        body: { rating, text },
      }),
      transformResponse: (response: ApiResponse<{ reviewId: number }> | null) => {
        if (!response || !response.data) {
          console.warn('completeFeedbackRequest: Empty or null response from API');
          return { reviewId: '' };
        }
        return { reviewId: String(response.data.reviewId) };
      },
      invalidatesTags: [
        { type: CACHE_TAGS.FeedbackRequests, id: 'LIST' },
        { type: CACHE_TAGS.Reviews, id: 'LIST' },
        { type: CACHE_TAGS.Profile, id: 'ME' },
        { type: CACHE_TAGS.MarketplaceNotifications, id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useCreateFeedbackRequestMutation,
  useGetPendingFeedbackRequestsQuery,
  useLazyGetPendingFeedbackRequestsQuery,
  useCompleteFeedbackRequestMutation,
} = feedbackApi;

